/**
 * Paste ghost drawing for notes (W2-03 note-lane; DESIGN.md §5.4 "Copy and paste", ARCHITECTURE.md §3.2).
 *
 * While a ghost paste is up (editor store `placement.kind === 'paste'`), the lane shows where the clipboard's notes
 * will land: the earliest item sits on the anchor (the snapped mouse beat, or the playhead when the mouse is outside
 * the timeline, plus the `[` / `]` shift), mirrored when M is on, shifted in X by the paste's X offset, repeated when
 * paste special asked for it. The ghost is drawn on the lane overlay only; nothing touches the document until the
 * paste is committed (click / Enter). Motion keys of the clipboard are ghosted by the motion columns (W2-04a) from the
 * same placement state; the lane draws a band over the pasted span so both read as one paste.
 *
 * `pasteGhostNotes` is pure (tested); `drawPasteGhost` paints it.
 */

import { Beats } from '../../core/time/beat';
import type { ClipboardPayload, PasteMode } from '../../core/commands/types';
import type { LineId } from '../../core/model/types';
import type { TimeAxis } from '../types';
import { glyphHalfPx, type LaneXMap } from './laneHit';
import { drawTag, outlineGlyph, paintGlyph, RULER_PX, type GlyphSpec, type LaneTheme } from './laneDraw';
import type { TimelineTheme } from './theme';

/** One ghost note in absolute beats (f64) and RPE X. */
export interface PasteGhostNote {
  readonly kind: GlyphSpec['kind'];
  readonly beat: number;
  readonly end: number;
  readonly x: number;
  readonly size: number;
  readonly fake: boolean;
  readonly below: boolean;
  readonly transparent: boolean;
}

export interface PasteGhostInput {
  readonly payload: ClipboardPayload;
  /** Beat (f64) the earliest clipboard item lands on (ignored with `mode.originalBeats`). */
  readonly anchor: number;
  readonly mode: PasteMode;
  /** RPE X added after mirroring. */
  readonly xShift: number;
  /** Line this lane shows. */
  readonly laneLine: LineId;
  /** Line the paste targets (Tab cycles it). */
  readonly targetLine: LineId;
}

export interface PasteGhost {
  readonly notes: readonly PasteGhostNote[];
  /** Pasted span [start, end] (f64 beats) over all repeats, for the band. */
  readonly start: number;
  readonly end: number;
  /** Notes that land on this lane / everywhere. */
  readonly here: number;
  readonly total: number;
}

/** Most ghost notes drawn (a huge clipboard still shows its span band and counts). */
export const MAX_GHOST_NOTES = 5000;

/**
 * Where the clipboard's notes land for a paste: absolute beats, X after mirroring and the X offset, every repeat.
 * Notes go to the target line, or to their source lines with `mode.toOriginalLines`; only the ones that land on
 * `laneLine` are returned (all are counted). Notes before beat 0 are skipped (the paste skips them too).
 */
export function pasteGhostNotes(input: PasteGhostInput): PasteGhost {
  const { payload, mode } = input;
  const origin = Beats.toF64(payload.origin);
  const anchor = mode.originalBeats ? origin : input.anchor;
  const span = Beats.toF64(payload.span);
  const repeat = Math.max(1, Math.floor(mode.repeat));
  // Same default as Core.clipboard.paste: the span, or one beat for a zero-length clipboard.
  const every = mode.every === undefined ? (span > 0 ? span : 1) : Beats.toF64(mode.every);
  const out: PasteGhostNote[] = [];
  let here = 0;
  let total = 0;
  for (let r = 0; r < repeat; r++) {
    const base = anchor + r * every;
    for (const cn of payload.notes) {
      const t = base + Beats.toF64(cn.note.t);
      if (t < -1e-9) continue;
      total++;
      const source = payload.lines[cn.lineSlot]?.sourceLine;
      const line = mode.toOriginalLines && source !== undefined ? source : input.targetLine;
      if (line !== input.laneLine) continue;
      here++;
      if (out.length >= MAX_GHOST_NOTES) continue;
      const end = cn.note.kind === 'hold' ? base + Beats.toF64(cn.note.end) : t;
      out.push({
        kind: cn.note.kind,
        beat: t,
        end,
        x: (mode.mirror ? -cn.note.x : cn.note.x) + input.xShift,
        size: cn.note.size,
        fake: cn.note.fake,
        below: !cn.note.above,
        transparent: cn.note.alpha <= 0,
      });
    }
  }
  return { notes: out, start: anchor, end: anchor + (repeat - 1) * every + span, here, total };
}

/** Opacity of the ghost's notes and of the band over the pasted span (CR-W3-05-2 item 4: clearly visible). */
export const GHOST_NOTE_ALPHA = 0.65;
export const GHOST_BAND_ALPHA = 0.16;

/**
 * Paints the ghost: a translucent band over the pasted span (keys included), the notes as dashed glyphs and a label
 * ("Paste 14 notes · M mirror · Tab line · Enter") at the top of the band.
 */
export function drawPasteGhost(
  ctx: CanvasRenderingContext2D,
  axis: TimeAxis,
  xmap: LaneXMap,
  width: number,
  ghost: PasteGhost,
  label: string | null,
  lane: LaneTheme,
  theme: TimelineTheme,
): void {
  const yStart = axis.yAtBeat(ghost.start);
  const yEnd = axis.yAtBeat(ghost.end);
  ctx.save();
  ctx.globalAlpha = GHOST_BAND_ALPHA;
  ctx.fillStyle = lane.ghost;
  const top = Math.min(yStart, yEnd);
  ctx.fillRect(0, top, width, Math.max(2, Math.abs(yStart - yEnd)));
  ctx.globalAlpha = 0.9;
  ctx.fillRect(0, Math.round(yStart), width, 1);
  ctx.fillRect(0, Math.round(yEnd), width, 1);
  ctx.restore();
  ctx.save();
  for (const n of ghost.notes) {
    const g: GlyphSpec = {
      kind: n.kind,
      cx: xmap.toPx(n.x),
      y: axis.yAtBeat(n.beat),
      yEnd: axis.yAtBeat(n.end),
      halfW: glyphHalfPx(n.size, xmap),
      fake: n.fake,
      below: n.below,
      transparent: n.transparent,
    };
    if (g.cx + g.halfW < 0 || g.cx - g.halfW > width) continue;
    if (Math.max(g.y, g.yEnd) < -20 || Math.min(g.y, g.yEnd) > axis.heightPx + 20) continue;
    ctx.globalAlpha = GHOST_NOTE_ALPHA;
    paintGlyph(ctx, g, lane);
    ctx.globalAlpha = 1;
    ctx.setLineDash([3, 2]);
    outlineGlyph(ctx, g, lane.ghost, 1.5, 1);
    ctx.setLineDash([]);
  }
  ctx.restore();
  if (label !== null) drawTag(ctx, 4, Math.max(RULER_PX + 18, Math.min(yStart, yEnd)), label, theme, lane);
}
